import { useEffect, useState } from "react"
import { motion, useMotionValue, useTransform, animate } from "framer-motion"
import { SwipeCardProps } from "../types"

interface Props extends SwipeCardProps {
  language?: "tr" | "en"
  isFirst?: boolean
}

export const SwipeCard = ({ id, text, onSwipe, isMobile, language = "en", isFirst = false }: Props) => {
  const x = useMotionValue(0)
  const rotate = useTransform(x, [-150, 150], [-8, 8])
  const opacity = useTransform(x, [-150, -80, 0, 80, 150], [0.4, 1, 1, 1, 0.4])
  const rightOpacity = useTransform(x, [0, 40], [0, 1])
  const leftOpacity = useTransform(x, [-40, 0], [1, 0])
  const [showHint, setShowHint] = useState(isFirst)
  const [hasSwipedOnce, setHasSwipedOnce] = useState(!isFirst)

  const rightLabel = language === "tr" ? "ÖNEMLİ →" : "IMPORTANT →"
  const leftLabel = language === "tr" ? "← BEKLEYEBİLİR" : "← CAN WAIT"
  const hintText = language === "tr" ? "Kartı kaydır" : "Swipe the card"

  useEffect(() => {
    if (!isFirst) return

    // Micro animation — kart hafif sağa sola hareket etsin
    const runAnimation = async () => {
      await animate(x, 18, { duration: 0.35, ease: "easeOut" })
      await animate(x, 0, { duration: 0.3, ease: "easeIn" })
      await new Promise(r => setTimeout(r, 300))
      await animate(x, -18, { duration: 0.35, ease: "easeOut" })
      await animate(x, 0, { duration: 0.3, ease: "easeIn" })
    }

    const timer = setTimeout(runAnimation, 600)
    return () => clearTimeout(timer)
  }, [isFirst])

  const handleDragEnd = (_: any, info: any) => {
    if (info.offset.x > 80) {
      setShowHint(false)
      setHasSwipedOnce(true)
      onSwipe("right")
    } else if (info.offset.x < -80) {
      setShowHint(false)
      setHasSwipedOnce(true)
      onSwipe("left")
    }
  }

  return (
    <div className="swipe-wrapper">
      <motion.div
        className="swipe-card"
        style={{ x, rotate, opacity }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.5}
        onDragEnd={handleDragEnd}
        whileTap={{ cursor: "grabbing" }}
      >
        {/* Sol label */}
        <motion.div
          style={{
            position: "absolute",
            top: "14px",
            left: "14px",
            fontSize: "11px",
            fontWeight: "700",
            letterSpacing: "0.1em",
            color: "var(--red)",
            background: "rgba(224,112,104,0.12)",
            padding: "5px 10px",
            borderRadius: "6px",
            opacity: showHint ? 0.5 : leftOpacity as any,
          }}
        >
          {leftLabel}
        </motion.div>

        {/* Sağ label */}
        <motion.div
          style={{
            position: "absolute",
            top: "14px",
            right: "14px",
            fontSize: "11px",
            fontWeight: "700",
            letterSpacing: "0.1em",
            color: "var(--green)",
            background: "rgba(93,184,150,0.12)",
            padding: "5px 10px",
            borderRadius: "6px",
            opacity: showHint ? 0.5 : rightOpacity as any,
          }}
        >
          {rightLabel}
        </motion.div>

        <span className="swipe-text" style={{ marginTop: "20px" }}>{text}</span>

        {/* Hint overlay */}
        {showHint && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "absolute",
              bottom: "14px",
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "12px",
              color: "var(--text-muted)",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
            }}
          >
            {hintText}
          </motion.div>
        )}
      </motion.div>

      {/* Desktop butonlar */}
      {!isMobile && (
        <div className="swipe-actions">
          <button className="btn-can-wait" onClick={() => { setShowHint(false); onSwipe("left") }}>
            {leftLabel}
          </button>
          <button className="btn-important" onClick={() => { setShowHint(false); onSwipe("right") }}>
            {rightLabel}
          </button>
        </div>
      )}
    </div>
  )
}